/**
 * <review-app>: holds the review app's widget (Judge.me, sections/product-reviews.liquid) and finishes dressing it as
 * the theme's reviews where CSS cannot (assets/reviews-app.css does the rest):
 * - its sort becomes the theme's custom select (`.gsel`, assets/custom-select.js), built from the `<template>` this
 *   element holds, with the app's own options. The app's select stays, hidden, and keeps sorting: the custom select
 *   sets its value and fires `change` on it;
 * - each reviewer's avatar gets their initial (`data-initial`), as on the theme's review cards, in place of the
 *   app's person icon;
 * - a long review shows its first lines only (`.is-long`, assets/reviews-app.css), with a « Voir plus » button
 *   (labels in `data-more` / `data-less`) that unfolds it.
 * The app renders its widget after the page, and renders its reviews again on each sort or page: a
 * MutationObserver keeps up.
 */

/** The sort select. The review form's « name format » select carries the same class, hence the wrapper. */
const SORT = '.jdgm-rev-widg__sort-wrapper select';
const REVIEW = '.jdgm-rev';
const AVATAR = '.jdgm-rev__icon';
const AUTHOR = '.jdgm-rev__author';
const BODY = '.jdgm-rev__body';
const MORE = '.av-more';

/** Lines a long review shows before « Voir plus » (the max-height of `.is-long` in assets/reviews-app.css). */
export const LINES = 6;

/**
 * Whether a review's text is long enough to fold: more than {@link LINES} lines, by over a line and a half, so the
 * button never hides a line or two only.
 *
 * @param {number} height - Height of the whole text, in px.
 * @param {number} lineHeight - In px.
 * @returns {boolean}
 */
export function isLong(height, lineHeight) {
  return lineHeight > 0 && height > lineHeight * (LINES + 1.5);
}

/**
 * First character of a name, upper-cased: a whole character, even an emoji. Empty for a blank name.
 *
 * @param {string | null | undefined} name
 * @returns {string}
 */
export function initial(name) {
  const [first = ''] = [...(name ?? '').trim()];
  return first.toUpperCase();
}

/**
 * Builds the custom select of a native `select` from `template`, whose first element is a `<custom-select>` with a
 * single `[role="option"]`: the model of each option, its first `<span>` taking the option's label.
 *
 * @param {HTMLSelectElement} select - Needs an id: the custom select drives it through `data-for`.
 * @param {HTMLTemplateElement} template
 * @returns {HTMLElement | null} null when the template holds no such custom select.
 */
export function buildSelect(select, template) {
  const control = document.importNode(template.content, true).firstElementChild;
  const model = control?.querySelector('[role="option"]');
  if (!(control instanceof HTMLElement) || !(model instanceof HTMLElement)) return null;
  control.dataset.for = select.id;
  for (const option of select.options) {
    const item = /** @type {HTMLElement} */ (model.cloneNode(true));
    item.dataset.value = option.value;
    item.setAttribute('aria-selected', String(option.selected));
    const label = item.querySelector('span');
    if (label) label.textContent = option.text.trim();
    model.before(item);
  }
  model.remove();
  const current = control.querySelector('.gsel-v');
  if (current) current.textContent = select.selectedOptions[0]?.text.trim() ?? '';
  return control;
}

export class ReviewApp extends HTMLElement {
  observer = new MutationObserver(() => this.update());

  /** The cards' width, hence the texts' length in lines, follows the window's. */
  resizer = new ResizeObserver(() => this.foldLongReviews());

  connectedCallback() {
    this.observer.observe(this, { childList: true, subtree: true });
    this.resizer.observe(this);
    this.addEventListener('click', this.onClick);
    this.update();
  }

  disconnectedCallback() {
    this.observer.disconnect();
    this.resizer.disconnect();
    this.removeEventListener('click', this.onClick);
  }

  update() {
    this.dressSort();
    this.dressAvatars();
    this.foldLongReviews();
  }

  /** Puts the custom select in place of the app's sort select, next to it: both go when the app re-renders. */
  dressSort() {
    const select = this.querySelector(SORT);
    const template = this.querySelector(':scope > template');
    if (!(select instanceof HTMLSelectElement) || !(template instanceof HTMLTemplateElement)) return;
    const native = select.closest('label') ?? select;
    if (native.hidden) return;
    select.id ||= `${this.id}-sort-select`;
    const control = buildSelect(select, template);
    if (!control) return;
    native.hidden = true;
    native.after(control);
  }

  dressAvatars() {
    for (const review of this.querySelectorAll(REVIEW)) {
      const avatar = review.querySelector(AVATAR);
      if (!(avatar instanceof HTMLElement) || avatar.dataset.initial) continue;
      const letter = initial(review.querySelector(AUTHOR)?.textContent);
      if (letter) avatar.dataset.initial = letter;
    }
  }

  /** Folds each long review under a « Voir plus » button, unfolded ones staying open. */
  foldLongReviews() {
    for (const body of this.querySelectorAll(BODY)) {
      if (!(body instanceof HTMLElement)) continue;
      const long = isLong(body.scrollHeight, parseFloat(getComputedStyle(body).lineHeight));
      const next = body.nextElementSibling;
      let button = next instanceof HTMLButtonElement && next.matches(MORE) ? next : null;
      if (long && !button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'av-more';
        button.setAttribute('aria-expanded', 'false');
        button.textContent = this.dataset.more ?? '';
        body.after(button);
      }
      if (button) button.hidden = !long;
      body.classList.toggle('is-long', long);
    }
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest(MORE) : null;
    const body = button?.previousElementSibling;
    if (!button || !(body instanceof HTMLElement)) return;
    const open = body.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(open));
    button.textContent = (open ? this.dataset.less : this.dataset.more) ?? '';
  };
}

if (!customElements.get('review-app')) customElements.define('review-app', ReviewApp);
